import { IAirtableAttachment } from "@domain/CRM/Attachment";
import { BoundaryChangeType } from "./BoundaryChangeTypes";
import { JobFlags } from "./JobFlags";
import { JobsPriority } from "./JobsPriorities";
import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { BaseDataModel } from "@domain/CRM/BaseDataModel";
import type { Deal } from "@domain/CRM/deal";
import { defaultJobForImport } from "../operations/defaultJobForImport";
import { updateJobFromDeal } from "../operations/updateJobFromDeal";

export enum JobStatusNumber {
	Unready = 0,
	Ready = 1,
	Sampled = 2,
	DropShipped = 3,
	CSVUnsent = 4,
	Completed = 7,
}

export interface ErrorNote {
	source: string;
	note: string;
}

export class Job extends BaseDataModel {
	readonly name!: string; // formula
	readonly edits!: string; // formula

	readonly status!: string; // formula
	readonly statusNumber!: JobStatusNumber; // formula
	sampleChanges!: string;
	boundaryAcres!: number;
	lastCrop?: string;
	nextCrop?: string;
	yieldGoal!: string;
	numberOfSamples?: number;

	fieldId?: string;
	readonly fieldName!: string; // GET: formula
	eventId!: string;

	jobFlags!: JobFlags[];
	dataSent!: string[];
	csvSentToLab!: boolean;
	boundaryChangeType?: BoundaryChangeType;
	boundaryChangeNotes?: string;
	fieldPriority!: JobsPriority;

	readonly testPackage!: string; // lookup from Deal
	readonly season!: string; // lookup from Deal
	readonly depth!: number; // lookup from Deal
	readonly density?: number; // lookup from Deal
	readonly frequencyOfMicros?: number; // lookup from Deal
	readonly labName?: string; // lookup from Deal
	microsList?: string;
	isBillingApproved!: boolean;
	isMarkedReady!: boolean;
	isBoundaryChanged!: boolean;
	isBoundaryApproved!: boolean;
	isPointsChanged!: boolean;
	isTestJob?: boolean;
	isPointsAttached!: boolean;
	isReadyToSample!: boolean;

	dateCreated!: Date;
	dateFileLastEdited?: Date;
	dateSampled?: Date;
	dateSampledManualOverride?: Date;
	dateShipped?: Date;
	dateLabResultsSent?: Date;
	dateFieldReady?: Date;
	dateFieldReadyEstimated?: Date;

	submissionNotes?: string;
	submitterEmail?: string;
	submitterName?: string;

	labSubmittalId!: string;
	readonly labAccountNumber?: string;
	labSubmissionCode?: string;
	labInstructions!: string;

	// Ops-curated SHP column hint. For customers whose Points SHPs use a non-standard sample-id
	// column (e.g. "sp_number" instead of one of the names in shared-ts ID_OPTIONS), this field
	// tells `Mission.load` which property to read as the sample id. Mirrors exit-interview-lambda's
	// `this.job.selectedSampleColumn` → `Mission.load(..., { selectedIdColumn })`. Without it, the
	// parser silently drops every point because it can't find a matching id column, and the diff
	// section ends up showing zero submitted samples.
	readonly selectedSampleColumn?: string;

	// Null on lite list payloads (which omit attachments to keep the fetch fast
	// and the payload small) until populated by `jobsList.ensureAttachments`.
	// {} means "loaded, no files". Guard reads with `?.` accordingly.
	attachments!: {
		pointsSHP?: IAirtableAttachment[];
		boundarySHP?: IAirtableAttachment[];
		boundaryGeoJSON?: IAirtableAttachment[];
		zonesSHP?: IAirtableAttachment[];
		executedPointsSHP?: IAirtableAttachment[];
		executedBoundarySHP?: IAirtableAttachment[];
		executedPointsBoundarySHP?: IAirtableAttachment[];
		labPDF?: IAirtableAttachment[];
		labCSV?: IAirtableAttachment[];
		labResults?: IAirtableAttachment[];
		fieldNotes?: IAirtableAttachment[];
		exeMission?: IAirtableAttachment[];
		intMission?: IAirtableAttachment[];
	} | null;

	/**
	 * Contains the file data itself. Primarily intended for creating new jobs.
	 */
	files!: {
		pointsSHP?: File[];
		pointsGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		boundarySHP?: File[];
		boundaryGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		zonesSHP?: File[];
		zonesGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		executedPointsSHP?: File[];
		executedBoundarySHP?: File[];
		executedPointsBoundarySHP?: File[];
		labPDF?: File[];
		labCSV?: File[];
		labResults?: File[];
		fieldNotes?: File[];
	};

	readonly companyName!: string; // lookup from Company
	readonly farmName?: string; // formula
	readonly growerName!: string; // lookup from Grower
	growerPhoneNumber?: string;

	branchIds!: string[];
	regionIds!: string[];
	dealIds!: string[];
	growerIds!: string[];
	salespersonIds!: string[];
	shiftId?: string;

	apiId?: string; // also used for creating new jobs
	softwareErrors!: ErrorNote[];
	softwareNotes!: ErrorNote[];
	url!: string;

	// used for creating new jobs
	importFieldName?: string;
	importDepth?: number;
	importDensity?: number;
	importLabName?: string;
	importFarmName?: string;
	importGrowerName?: string;
	importBranchName?: string;

	externalId?: string;
	externalTestPackageId?: string;
	externalFrequencyOfMicros?: number;
	externalLabId?: string;
	externalFarmId?: string;
	thirdPartyCustomerIdForDealSearch?: string;
	thirdPartyLocationId?: string;
	labCode?: string;
	externalGrower?: {
		id: string;
		name: string;
	};
	newSalesperson?: {
		name: string;
		phone: string;
	};
	collectBoundary?: boolean;

	// Skip Management — populated on jobs that went through the skips workflow; absent on normal jobs.
	exeSamplesSkipped?: number;
	skipsInExe?: string; // "None" | "Permanent Skip" | "Temporary Skip"
	pulledPercentSkipMgmt?: number; // 0–1 decimal (multiply by 100 for display)
	previouslySampledExeSkipMgmt?: number;
	contJobIds!: string[]; // continuation job (set on the archive job)
	archiveJobIds!: string[]; // archive job (set on the continuation job)

	/** Pass-through to {@link defaultJobForImport} in `../operations/defaultJobForImport`. */
	static defaultForImport(
		fieldName?: string,
		depth?: number,
		density?: number,
		growerName?: string,
		frequencyOfMicros?: number,
		testPackage?: string,
		farmName?: string,
		labName?: string,
	): Job {
		return defaultJobForImport(
			fieldName,
			depth,
			density,
			growerName,
			frequencyOfMicros,
			testPackage,
			farmName,
			labName,
		);
	}

	/** Pass-through to {@link updateJobFromDeal} in `../operations/updateJobFromDeal`. */
	static updateFromDeal(job: Job, deal: Deal): void {
		updateJobFromDeal(job, deal);
	}
}
